import { useState, useEffect, useCallback, useRef } from 'react';
import { Send, Trash2, Copy, Clock, Users, Inbox, Clipboard, Link, FileCode, FileText, ChevronLeft } from 'lucide-react';
import PageHeader from '../components/PageHeader.jsx';
import EmptyState from '../components/EmptyState.jsx';
import { api } from '../services/api.js';
import { useApi } from '../hooks/useApi.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useNotificationRefresh } from '../hooks/useNotificationRefresh.js';
import { useToast } from '../contexts/ToastContext.jsx';
import { formatDate, cn } from '../lib/utils.js';

const CLIP_TYPES = [
  { value: 'text', label: 'Text', icon: FileText },
  { value: 'code', label: 'Code', icon: FileCode },
  { value: 'url', label: 'URL', icon: Link },
];

const detectClipType = (content) => {
  if (!content) return 'text';
  const trimmed = content.trim();
  if (/^https?:\/\//.test(trimmed) || /^www\./.test(trimmed)) return 'url';
  
  const codePatterns = [
    /[{};]/, /==/, /\bfunction\b/, /\bconst\b/, /\blet\b/, /\bvar\b/,
    /\bif\s*\(/, /\bfor\s*\(/, /\bwhile\s*\(/, /\breturn\b/,
    /\bsudo\b/, /\bssh\b/, /\bdocker\b/, /\bkubectl\b/,
    /\bshow\s+(run|ip)\b/, /^#!\//m, /\bimport\b.*from/, /\bexport\b/, /=>/, /\|\|/, /&&/,
  ];
  
  const lines = trimmed.split('\n');
  const hasMultipleLines = lines.length > 1;
  const hasIndentation = lines.some(line => /^\s{2,}/.test(line));
  const hasCodePattern = codePatterns.some(pattern => pattern.test(trimmed));
  
  if (hasCodePattern || (hasMultipleLines && hasIndentation)) return 'code';
  return 'text';
};

const getTypeBadge = (type) => {
  const badge = CLIP_TYPES.find(t => t.value === type) || CLIP_TYPES[0];
  const Icon = badge.icon;
  
  return (
    <span className={cn(
      'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-medium',
      type === 'code' && 'bg-emerald-500/20 text-emerald-400',
      type === 'url' && 'bg-blue-500/20 text-blue-400',
      type === 'text' && 'bg-zinc-500/20 text-zinc-400',
    )}>
      <Icon size={9} />
      {badge.label}
    </span>
  );
};

export default function Shared() {
  const toast = useToast();
  const { user } = useAuth();
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const messagesEndRef = useRef(null);

  // 1. Fetch users list
  const { data: usersData, loading: usersLoading } = useApi(
    () => api.listUsers(),
    []
  );

  // 2. Fetch conversation history for selected user
  const { data: chatData, loading: chatLoading, reload: reloadChat } = useApi(
    () => selectedUserId ? api.listSharedClips(`withUser=${selectedUserId}`) : Promise.resolve({ data: { items: [] } }),
    [selectedUserId]
  );

  // Real-time updates
  useNotificationRefresh(reloadChat, 'syncvault:refresh:shared');

  // Trigger tick every 10s to update remaining expiration timers
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(Date.now()), 10000);
    return () => clearInterval(timer);
  }, []);

  const messages = chatData?.data?.items || [];
  const users = usersData?.data?.items || [];
  const selectedUser = users.find(u => u.id === selectedUserId);

  // Scroll to bottom helper
  const scrollToBottom = useCallback((behavior = 'smooth') => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior });
    }
  }, []);

  // Scroll to bottom on new messages or chat select
  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom('smooth');
    }
  }, [messages.length, scrollToBottom]);

  // Scroll to bottom instantly on first select
  useEffect(() => {
    if (selectedUserId) {
      setTimeout(() => scrollToBottom('auto'), 100);
    }
  }, [selectedUserId, scrollToBottom]);

  const handleSend = async (e) => {
    if (e) e.preventDefault();
    if (!selectedUserId) return;
    if (!content.trim()) return;

    setSending(true);
    try {
      const detectedType = detectClipType(content);
      const res = await api.sendSharedClip({
        recipientId: selectedUserId,
        content,
        type: detectedType,
        source: 'web'
      });
      setContent('');
      await reloadChat();
      scrollToBottom('smooth');
    } catch (err) {
      toast.error(err.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleCopy = async (clipId, text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try { document.execCommand('copy'); } catch { toast.error('Could not copy'); }
      document.body.removeChild(textarea);
    }
    setCopiedId(clipId);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedId(null), 1500);
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete/Dismiss this message? This cannot be undone.')) return;
    try {
      await api.deleteSharedClip(id);
      toast.info('Message deleted');
      reloadChat();
    } catch (err) {
      toast.error(err.message || 'Failed to delete message');
    }
  };

  const handleClearChat = async () => {
    if (!selectedUserId) return;
    if (!confirm(`Are you sure you want to clear all chat history with @${selectedUser.username}?\n\nThis will permanently delete these messages for both users to save database storage.`)) return;

    try {
      await api.clearChat(selectedUserId);
      toast.info('Chat history cleared');
      reloadChat();
    } catch (err) {
      toast.error(err.message || 'Failed to clear chat');
    }
  };

  const getExpirationText = (createdAtStr) => {
    const created = new Date(createdAtStr).getTime();
    const expiry = created + 24 * 3600 * 1000;
    const diffMs = expiry - currentTime;

    if (diffMs <= 0) return 'Expired';
    const hours = Math.floor(diffMs / (3600 * 1000));
    const mins = Math.floor((diffMs % (3600 * 1000)) / (60 * 1000));

    if (hours > 0) {
      return `${hours}h ${mins}m left`;
    }
    return `${mins}m left`;
  };

  return (
    <div className="flex h-[calc(100vh-8.5rem)] w-full overflow-hidden rounded-xl border border-border/40 bg-panel/20 backdrop-blur-sm">
      {/* LEFT SIDEBAR: Users list */}
      <div className={cn(
        "w-full md:w-64 border-r border-border/40 flex flex-col bg-zinc-950/20",
        selectedUserId !== null ? "hidden md:flex" : "flex"
      )}>
        <div className="p-3 border-b border-border/40 flex items-center justify-between">
          <h3 className="text-xs font-semibold text-zinc-400 flex items-center gap-1.5 uppercase tracking-wider">
            <Users size={14} className="text-accent" />
            <span>Contacts</span>
          </h3>
          <span className="chip bg-zinc-800/40 text-[10px]">{users.length}</span>
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {usersLoading && users.length === 0 ? (
            <div className="text-center text-xs text-zinc-500 py-4 italic">Loading contacts...</div>
          ) : users.length === 0 ? (
            <div className="text-center text-xs text-zinc-600 py-4">No contacts found</div>
          ) : (
            users.map((u) => (
              <button
                key={u.id}
                onClick={() => setSelectedUserId(u.id)}
                className={cn(
                  'w-full flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm transition-all duration-150',
                  selectedUserId === u.id
                    ? 'bg-accent/15 text-accent-light font-medium'
                    : 'text-zinc-400 hover:bg-white/5 hover:text-zinc-200'
                )}
              >
                <div className="h-7 w-7 rounded-full bg-accent/20 border border-accent/20 flex items-center justify-center font-bold text-accent text-xs">
                  {u.username.substring(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">@{u.username}</div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* RIGHT SIDEBAR: Chat Area */}
      <div className={cn(
        "flex-1 flex flex-col bg-zinc-950/10",
        selectedUserId === null ? "hidden md:flex" : "flex"
      )}>
        {selectedUser ? (
          <>
            {/* Chat Header */}
            <div className="px-4 py-3 border-b border-border/40 flex items-center justify-between bg-zinc-950/30">
              <div className="flex items-center gap-2.5">
                <button
                  type="button"
                  onClick={() => setSelectedUserId(null)}
                  className="p-1 hover:bg-white/5 text-zinc-400 hover:text-zinc-200 rounded-lg md:hidden mr-1"
                  title="Back to contacts"
                >
                  <ChevronLeft size={20} />
                </button>
                <div className="h-8 w-8 rounded-full bg-accent/25 border border-accent/20 flex items-center justify-center font-bold text-accent text-sm">
                  {selectedUser.username.substring(0, 2).toUpperCase()}
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-zinc-200">@{selectedUser.username}</h2>
                  <span className="text-[10px] text-zinc-500 flex items-center gap-1">
                    <Clock size={10} /> Auto-delete active
                  </span>
                </div>
              </div>

              {messages.length > 0 && (
                <button
                  type="button"
                  onClick={handleClearChat}
                  className="btn-danger !px-2.5 !py-1.5 flex items-center gap-1 text-xs"
                  title="Clear Chat History"
                >
                  <Trash2 size={12} /> Clear Chat
                </button>
              )}
            </div>

            {/* Chat Messages Feed */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {chatLoading && messages.length === 0 ? (
                <div className="text-center text-xs text-zinc-500 py-4 italic">Loading conversation...</div>
              ) : messages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-zinc-500 p-8">
                  <Inbox size={32} className="text-zinc-600 mb-2" />
                  <p className="text-xs">No messages shared yet.</p>
                  <p className="text-[10px] text-zinc-600 mt-1">Start the conversation below.</p>
                </div>
              ) : (
                messages.map((m) => {
                  const isMe = m.sender_id === user.id;
                  return (
                    <div
                      key={m.id}
                      className={cn(
                        'flex w-full',
                        isMe ? 'justify-end' : 'justify-start'
                      )}
                    >
                      <div
                        className={cn(
                          'max-w-[70%] rounded-xl px-3 py-2.5 shadow-sm text-sm group relative border',
                          isMe 
                            ? 'bg-accent/15 border-accent/20 text-zinc-100 rounded-tr-none' 
                            : 'bg-zinc-900 border-zinc-800 text-zinc-100 rounded-tl-none',
                          copiedId === m.id && 'ring-1 ring-accent-light'
                        )}
                      >
                        {/* Clip text */}
                        <pre
                          className={cn(
                            'whitespace-pre-wrap break-words leading-relaxed',
                            m.type === 'code' && 'font-mono bg-black/35 p-2 rounded text-xs border border-zinc-950 mt-1'
                          )}
                        >
                          {m.content}
                        </pre>

                        {/* Footer row: Badge & Time */}
                        <div className="mt-1.5 flex items-center justify-between gap-4 text-[9px] text-zinc-500 select-none">
                          <div className="flex items-center gap-1.5">
                            {getTypeBadge(m.type || 'text')}
                          </div>
                          <span>{formatDate(m.created_at)}</span>
                        </div>

                        {/* Hover Quick Actions */}
                        <div
                          className={cn(
                            'absolute top-2 opacity-0 group-hover:opacity-100 transition-opacity duration-150 flex items-center gap-1 bg-zinc-900/90 border border-zinc-800 p-0.5 rounded-md shadow-lg',
                            isMe ? '-left-12' : '-right-12'
                          )}
                        >
                          <button
                            onClick={() => handleCopy(m.id, m.content)}
                            className="p-1 hover:text-accent-light text-zinc-400 rounded transition"
                            title="Copy to clipboard"
                          >
                            <Copy size={11} />
                          </button>
                          <button
                            onClick={() => handleDelete(m.id)}
                            className="p-1 hover:text-rose-400 text-zinc-400 rounded transition"
                            title="Delete"
                          >
                            <Trash2 size={11} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Chat Send Input Box */}
            <form onSubmit={handleSend} className="p-3 border-t border-border/40 bg-zinc-950/20 flex gap-2">
              <textarea
                className="input flex-1 min-h-[40px] max-h-[120px] text-sm resize-none py-2"
                placeholder={`Send a clip to @${selectedUser.username}... (Press Enter to send)`}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={1}
              />
              <button
                type="submit"
                disabled={sending || !content.trim()}
                className="btn-primary shrink-0 self-end !p-2 rounded-lg"
              >
                <Send size={15} />
              </button>
            </form>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-zinc-500 p-8">
            <Inbox size={48} className="text-zinc-700 mb-3" />
            <h3 className="font-semibold text-zinc-300">No Chat Selected</h3>
            <p className="text-xs text-zinc-500 mt-1 max-w-xs text-center">
              Select a contact from the sidebar to view shared clipboard messages and start sharing.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
